import { LazyMotion, domAnimation, m, useReducedMotion } from "motion/react";
import { useScrollReveal } from "../ds/core/motion";
import {
  Clock, Users2, Zap, type LucideIcon,
  Fingerprint, MapPinned, Coffee, ShieldCheck, AlarmClock, FileSpreadsheet,
  Palmtree, CalendarX2, Timer, Gauge, UserCog, FileClock,
  Route, Radar, Webhook, FolderKanban, CalendarRange,
} from "lucide-react";
import { Container } from "./ui";
import { useLocale } from "../i18n/useLocale";
import { useActiveLocale } from "../i18n/LocaleContext";
import { localePath } from "../i18n/localePath";

/**
 * FeatureOverview — a scannable catalogue of every feature, grouped into three
 * families. This is the "quick scan of everything" the user does FIRST; the
 * detail lives in the sections below / on the Features page, so the cards are
 * NOT interactive accordions (no duplicated detail) — each card is a quiet link
 * that bridges to where that detail is told. Cards fade up on scroll (stagger).
 */

type Plan = "ALL PLANS" | "BASIC+" | "PRO+" | "TIME PRO+" | "PROJECT" | "SOON" | "";
type Feat = { icon: LucideIcon; name: string; plan: Plan; desc: string; star?: boolean; law2027?: boolean };
type Family = {
  key: string;
  label: string;
  icon: LucideIcon;
  theme: { chip: string; iconBg: string; iconFg: string };
  feats: Feat[];
};

const BLUE = { chip: "text-ds-accent", iconBg: "bg-ds-tint", iconFg: "text-ds-accent" };
const GOLD = { chip: "text-ds-gold-ink", iconBg: "bg-ds-gold-soft", iconFg: "text-ds-gold-ink" };
const GREEN = { chip: "text-ds-pos", iconBg: "bg-ds-mint-soft", iconFg: "text-ds-pos" };

type TFn = ReturnType<typeof useLocale>["t"];

function families(isBelgium: boolean, t: TFn): Family[] {
  return [
    {
      key: "time", label: t("features:ov.time.label"), icon: Clock, theme: BLUE,
      feats: [
        { icon: Fingerprint, name: t("features:ov.time.clockin.name"), plan: "ALL PLANS", law2027: isBelgium, desc: t("features:ov.time.clockin.desc") },
        { icon: MapPinned, name: t("features:ov.time.gps.name"), plan: "BASIC+", star: true, law2027: isBelgium, desc: t("features:ov.time.gps.desc") },
        { icon: Coffee, name: t("features:ov.time.break.name"), plan: "ALL PLANS", desc: t("features:ov.time.break.desc") },
        { icon: ShieldCheck, name: t("features:ov.time.tamper.name"), plan: "ALL PLANS", law2027: isBelgium, desc: isBelgium ? t("features:ov.time.tamper.desc.be") : t("features:ov.time.tamper.desc.intl") },
        { icon: AlarmClock, name: t("features:ov.time.overtime.name"), plan: "BASIC+", desc: t("features:ov.time.overtime.desc") },
        { icon: FileSpreadsheet, name: t("features:ov.time.payroll.name"), plan: "BASIC+", desc: isBelgium ? t("features:ov.time.payroll.desc.be") : t("features:ov.time.payroll.desc.intl") },
      ],
    },
    {
      key: "people", label: t("features:ov.people.label"), icon: Users2, theme: GOLD,
      feats: [
        { icon: Palmtree, name: t("features:ov.people.leave.name"), plan: "BASIC+", desc: t("features:ov.people.leave.desc") },
        { icon: CalendarX2, name: t("features:ov.people.absence.name"), plan: "BASIC+", desc: t("features:ov.people.absence.desc") },
        { icon: Timer, name: t("features:ov.people.overtime.name"), plan: "BASIC+", desc: t("features:ov.people.overtime.desc") },
        { icon: Gauge, name: t("features:ov.people.dashboard.name"), plan: "ALL PLANS", desc: t("features:ov.people.dashboard.desc") },
        { icon: UserCog, name: t("features:ov.people.selfservice.name"), plan: "ALL PLANS", desc: t("features:ov.people.selfservice.desc") },
        { icon: FileClock, name: t("features:ov.people.reports.name"), plan: "BASIC+", desc: t("features:ov.people.reports.desc") },
      ],
    },
    {
      key: "advanced", label: t("features:ov.advanced.label"), icon: Zap, theme: GREEN,
      feats: [
        { icon: Route, name: t("features:ov.advanced.mileage.name"), plan: "PRO+", star: true, desc: t("features:ov.advanced.mileage.desc") },
        { icon: Radar, name: t("features:ov.advanced.track.name"), plan: "PRO+", star: true, desc: t("features:ov.advanced.track.desc") },
        { icon: Webhook, name: t("features:ov.advanced.api.name"), plan: "TIME PRO+", desc: t("features:ov.advanced.api.desc") },
        { icon: FolderKanban, name: t("features:ov.advanced.project.name"), plan: "PROJECT", star: true, desc: t("features:ov.advanced.project.desc") },
        { icon: Clock, name: t("features:ov.advanced.cost.name"), plan: "PROJECT", desc: t("features:ov.advanced.cost.desc") },
        { icon: CalendarRange, name: t("features:ov.advanced.planning.name"), plan: "SOON", desc: t("features:ov.advanced.planning.desc") },
      ],
    },
  ];
}

function planClass(plan: Plan, chip: string) {
  if (plan === "PROJECT") return "text-ds-pos";
  if (plan === "SOON") return "text-ink-faint";
  return chip;
}

function FeatureCard({ f, theme, href, delay }: {
  f: Feat; theme: Family["theme"]; href?: string; delay: number;
}) {
  const Icon = f.icon;
  const { t } = useLocale();
  const soon = f.plan === "SOON";
  // These cards bridge via a raw motion-anchor (full reload), not an RR <Link>,
  // so keep them inside the active locale too (AC4): prefix the internal href.
  const localeHref = href ? localePath(useActiveLocale()?.code, href) : undefined;
  // The scroll reveal — resting state in the prerender, hidden only off screen and
  // only once the app runs (ds/core/motion.tsx, DIT-553).
  const reveal = useScrollReveal<HTMLElement>({
    from: { opacity: 0, y: 16 },
    to: { opacity: 1, y: 0 },
    transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1], delay },
  });
  const motionProps = {
    ...reveal,
    // DS card: grey fill, thick white border (depth via the border, not shadow).
    // On hover the fill drops to transparent so the blue panel shows through the
    // white-bordered frame, and the content cross-fades to a white description.
    className: `group relative flex h-full flex-col overflow-hidden rounded-2xl border-[6px] border-white bg-bg-subtle text-left shadow-[0_1px_3px_rgba(15,23,42,0.05)] transition-all duration-300 ease-out hover:-translate-y-1 hover:bg-transparent hover:shadow-none ${soon ? "opacity-60" : ""} ${href ? "cursor-pointer" : ""}`,
  };
  const inner = (
    <>
      {/* Top-right marker — 2027 requirement (or differentiator dot), pinned to the
          corner so it never shifts the card layout. */}
      {f.law2027 ? (
        <span className="absolute right-2 top-2 z-10 inline-flex items-center rounded-full bg-ds-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-ds-accent transition-opacity duration-300 group-hover:opacity-0">2027</span>
      ) : f.star ? (
        <span className="absolute right-2.5 top-2.5 z-10 h-1.5 w-1.5 rounded-full bg-ds-accent transition-opacity duration-300 group-hover:opacity-0" title={t("features:ov.differentiator")} />
      ) : null}

      {/* Resting — icon, name, plan tag (fades out on hover) */}
      <div className="flex flex-1 flex-col items-start gap-3 p-4 transition-opacity duration-300 ease-out group-hover:opacity-0">
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl bg-white ${theme.iconFg}`}>
          <Icon size={20} strokeWidth={2} />
        </span>
        <span className="hyphens-auto break-words text-ds-14 font-semibold leading-tight text-ds-ink">{f.name}</span>
        {f.plan && (
          <span className={`mt-auto inline-flex items-center rounded-full border border-line bg-surface px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] ${planClass(f.plan, theme.chip)}`}>{f.plan}</span>
        )}
      </div>

      {/* Hover — blue card, text only (no icon), anchored bottom-left */}
      <div className="pointer-events-none absolute inset-0 flex flex-col items-start justify-end gap-2 p-4 opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100">
        <span className="hyphens-auto break-words text-ds-14 font-semibold leading-tight text-white">{f.name}</span>
        <span className="line-clamp-6 hyphens-auto break-words text-[12px] leading-tight text-white/85">{f.desc}</span>
      </div>
    </>
  );
  // Linked (home / projects → /features) or plain (already on the features page).
  return localeHref
    ? <m.a href={localeHref} {...motionProps} ref={reveal.ref as React.RefObject<HTMLAnchorElement>}>{inner}</m.a>
    : <m.div {...motionProps} ref={reveal.ref as React.RefObject<HTMLDivElement>}>{inner}</m.div>;
}

export default function FeatureOverview({
  detailHref = "/features",
  compact = false,
}: {
  /** Where a card click bridges to (the detail lives there, not in the card). */
  detailHref?: string;
  /** Tighter top/bottom padding when used as a home-page teaser. */
  compact?: boolean;
}) {
  const { market, t } = useLocale();
  const reduce = useReducedMotion() ?? false;
  const fams = families(market === "belgium", t);

  return (
    <section className="bg-bg">
      {/* Brand-blue gradient panel (faint grid), inset 12px from the page edges */}
      <div className="px-4 py-3">
        <div
          className="relative overflow-hidden rounded-[32px]"
          style={{ background: "linear-gradient(to bottom, var(--ds-accent-deep), var(--ds-accent))" }}
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
              backgroundSize: "64px 64px",
              maskImage: "linear-gradient(to bottom, black, transparent)",
              WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
            }}
          />
      <Container className={`relative ${compact ? "py-14" : "py-16 md:py-20"}`}>
        <div className="max-w-2xl">
          <p className="text-ds-14 font-semibold uppercase tracking-[0.14em] text-white/70">{t("features:ov.eyebrow")}</p>
          <h2 className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-[34px]">
            {t("features:ov.title")}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-white/85">
            {t("features:ov.intro")}
          </p>
        </div>

        <LazyMotion features={domAnimation}>
          <div className="mt-10 flex flex-col gap-10">
            {fams.map((fam) => {
              const Fi = fam.icon;
              return (
                <div key={fam.key}>
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5">
                    <Fi size={15} strokeWidth={2} className="text-white" />
                    <span className="text-[12px] font-bold uppercase tracking-[0.1em] text-white">{fam.label}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {fam.feats.map((f, i) => (
                      <FeatureCard key={f.name} f={f} theme={fam.theme} href={detailHref} delay={reduce ? 0 : i * 0.04} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </LazyMotion>
      </Container>
        </div>
      </div>
    </section>
  );
}
